import { Extension } from '@tiptap/core'
import { Fragment, Slice } from '@tiptap/pm/model'
import { Plugin, TextSelection } from '@tiptap/pm/state'
import { canSplit } from '@tiptap/pm/transform'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { closeHistory, history, isHistoryTransaction } from '@tiptap/pm/history'

export const TASK_ATTRIBUTES = [
  'data-meeting-task', 'data-meeting-task-people', 'data-meeting-task-due',
  'data-meeting-task-title', 'data-meeting-task-mode'
]
const validMetadata = metadata => metadata && typeof metadata === 'object' &&
  Object.entries(metadata).every(([key, value]) => TASK_ATTRIBUTES.slice(1).includes(key) && typeof value === 'string')
const emptyAttributes = () => Object.fromEntries(TASK_ATTRIBUTES.map(name => [name, null]))

function paragraphAt($position) {
  return $position.parent.type.name === 'paragraph'
    ? { node: $position.parent, position: $position.before() }
    : null
}

function selectedParagraph(state) {
  const { selection } = state
  if (!(selection instanceof TextSelection)) return null
  let target = paragraphAt(selection.$to)
  if (!selection.empty && selection.$to.parentOffset === 0) {
    // The next block's start is outside the selected text, including in lists.
    target = null
    state.doc.nodesBetween(selection.from, selection.to, (node, position) => {
      if (node.isTextblock && position + 1 < selection.to) target = { node, position }
    })
    if (target?.node.type.name !== 'paragraph') return null
  }
  return target && {
    ...target,
    canCreate: !!target.node.textContent.trim() && !target.node.attrs['data-meeting-task']
  }
}

function markers(doc) {
  const found = []
  doc.descendants((node, position) => {
    if (node.type.name === 'paragraph' && node.attrs['data-meeting-task']) {
      found.push({ node, position, token: node.attrs['data-meeting-task'] })
    }
  })
  return found
}

function stripOwnership(fragment) {
  const children = []
  fragment.forEach(node => {
    children.push(node.isText
      ? node
      : node.type.create(
        { ...node.attrs, ...Object.fromEntries(TASK_ATTRIBUTES.filter(key => key in node.attrs).map(key => [key, null])) },
        stripOwnership(node.content), node.marks
      ))
  })
  return Fragment.fromArray(children)
}

/** Trusted, explicitly selected host extension; all Editor state stays in this closure. */
export function createMeetingInlineTasks({ options = {} } = {}) {
  let prepared = null
  let context = null
  let titleHistory = null
  const detailFor = node => ({
    token: node.attrs['data-meeting-task'],
    source: node.textContent,
    title: node.attrs['data-meeting-task-title'] || '',
    people: (node.attrs['data-meeting-task-people'] || '').split(',').filter(Boolean).map(id => `person.${id}`),
    due: node.attrs['data-meeting-task-due'] || '',
    mode: node.attrs['data-meeting-task-mode'] || 'pending'
  })
  const dispatchOpen = (editor, detail) => {
    if (!editor.isEditable || !editor.view.dom.isConnected ||
      editor.view.dom.closest('[data-rich-text-editor-editable="false"]')) return false
    titleHistory = null
    editor.view.dom.dispatchEvent(new CustomEvent('meeting-inline-task-open', { bubbles: true, detail }))
    return true
  }
  const open = (editor, token) => {
    const block = markers(editor.state.doc).find(block => block.token === token)
    if (block) dispatchOpen(editor, detailFor(block.node))
  }
  const announce = (editor) => {
    if (!editor.view?.dom?.isConnected) return
    const target = selectedParagraph(editor.state)
    const token = editor.state.selection.empty ? target?.node.attrs['data-meeting-task'] : null
    editor.view.dom.dispatchEvent(new CustomEvent('meeting-inline-task-selection', {
      bubbles: true,
      detail: { ...(token ? detailFor(target.node) : {}), canCreate: target?.canCreate === true }
    }))
  }
  const refuse = (editor) => {
    editor.view.dom.dispatchEvent(new CustomEvent('meeting-inline-task-refused', {
      bubbles: true, detail: { message: options.recoveryMessage }
    }))
    return false
  }

  return Extension.create({
    name: 'meeting-inline-tasks',
    priority: 1000,
    addKeyboardShortcuts() {
      return {
        Enter: () => {
          if (!this.editor.state.selection.$from.parent.attrs['data-meeting-task']) return false
          this.editor.commands.splitMeetingTask()
          return true
        }
      }
    },
    addGlobalAttributes() {
      return [{
        types: ['paragraph'],
        attributes: Object.fromEntries(TASK_ATTRIBUTES.map(name => [name, {
          default: null,
          keepOnSplit: false,
          parseHTML: element => element.getAttribute(name),
          renderHTML: attributes => attributes[name] ? { [name]: attributes[name] } : {}
        }]))
      }]
    },
    addCommands() {
      return {
        splitMeetingTask: () => ({ editor, state, tr }) => {
          const { $from, $to } = state.selection
          if (!$from.sameParent($to) || !$from.parent.attrs['data-meeting-task'] || !$from.parentOffset) return refuse(editor)
          const paragraph = { type: $from.parent.type, attrs: { ...$from.parent.attrs, ...emptyAttributes() } }
          const inList = $from.depth > 1 && $from.node(-1).type.name === 'listItem'
          const types = inList ? [{ type: $from.node(-1).type, attrs: $from.node(-1).attrs }, paragraph] : [paragraph]
          tr.deleteSelection()
          const position = tr.mapping.map($from.pos)
          if (!canSplit(tr.doc, position, types.length, types)) return refuse(editor)
          tr.split(position, types.length, types).scrollIntoView()
          return true
        },
        prepareMeetingTask: nonce => ({ state }) => {
          const target = selectedParagraph(state)
          prepared = null
          if (!target?.canCreate) return false
          prepared = { nonce, doc: state.doc, position: target.position }
          return true
        },
        stageMeetingTask: (token, nonce) => ({ editor, state }) => {
          const target = selectedParagraph(state)
          if (!prepared || prepared.nonce !== nonce || prepared.doc !== state.doc ||
            !target?.canCreate || target.position !== prepared.position) return false
          prepared.token = token
          return dispatchOpen(editor, { ...detailFor(target.node), token, nonce, isNew: true })
        },
        cancelMeetingTask: token => () => {
          if (prepared?.token === token) prepared = null
          return true
        },
        markMeetingTask: (token, nonce, metadata = {}) => ({ state, tr }) => {
          if (!validMetadata(metadata) || (prepared?.token && prepared.token !== token)) return false
          if (!prepared || prepared.nonce !== nonce || prepared.doc !== state.doc) return false
          const target = selectedParagraph(state)
          if (!target?.canCreate || target.position !== prepared.position) return false
          closeHistory(tr).setNodeMarkup(prepared.position, undefined, {
            ...target.node.attrs, ...emptyAttributes(), ...metadata, 'data-meeting-task': token
          })
          if (!prepared.token) tr.setMeta('meetingTaskOpen', token)
          prepared = null
          return true
        },
        setMeetingTask: (token, metadata) => ({ state, tr }) => {
          const block = markers(state.doc).find(block => block.token === token)
          if (!block || !metadata || typeof metadata !== 'object') return false
          if (!validMetadata(metadata)) return false
          const titleOnly = Object.keys(metadata).length === 1 && 'data-meeting-task-title' in metadata
          if (titleOnly && titleHistory?.token === token && titleHistory.doc === state.doc) {
            // Keep this contiguous title edit together even across a typing pause.
            tr.setTime(titleHistory.time)
          } else {
            closeHistory(tr)
          }
          tr.setNodeMarkup(block.position, undefined, { ...block.node.attrs, ...metadata })
          titleHistory = titleOnly ? { token, time: tr.time, doc: tr.doc } : null
          return true
        },
        finishMeetingTaskEdit: () => ({ tr }) => {
          titleHistory = null
          closeHistory(tr)
          return true
        },
        unmarkMeetingTask: token => ({ state, tr }) => {
          const block = markers(state.doc).find(block => block.token === token)
          if (!block) return false
          closeHistory(tr).setMeta('meetingTaskUnmark', token)
            .setNodeMarkup(block.position, undefined, { ...block.node.attrs, ...emptyAttributes() })
          return true
        },
        setMeetingTaskContext: key => ({ editor }) => {
          if (key !== context) {
            // A different occurrence must never inherit the previous occurrence's undo ownership.
            prepared = null
            titleHistory = null
            context = key
            editor.unregisterPlugin('history')
            editor.registerPlugin(history())
          }
          return true
        }
      }
    },
    onCreate() { announce(this.editor) },
    onSelectionUpdate() { announce(this.editor) },
    onTransaction({ transaction }) {
      announce(this.editor)
      const token = transaction.getMeta('meetingTaskOpen')
      if (token) open(this.editor, token)
    },
    addProseMirrorPlugins() {
      const editor = this.editor
      return [new Plugin({
        filterTransaction(tr, state) {
          if (!tr.docChanged || tr.getMeta('preventUpdate')) return true
          const before = markers(state.doc)
          const after = markers(tr.doc)
          const ids = after.map(block => block.token)
          if (new Set(ids).size !== ids.length) return refuse(editor)
          if (after.some(block => !block.node.textContent.trim())) return refuse(editor)
          if (isHistoryTransaction(tr)) return true
          if (before.some(block => !ids.includes(block.token) && tr.getMeta('meetingTaskUnmark') !== block.token)) {
            return refuse(editor)
          }
          return true
        },
        props: {
          transformPasted: slice => new Slice(stripOwnership(slice.content), slice.openStart, slice.openEnd),
          transformCopied: slice => new Slice(stripOwnership(slice.content), slice.openStart, slice.openEnd),
          decorations(state) {
            return DecorationSet.create(state.doc, markers(state.doc).map(block => Decoration.widget(
              block.position + block.node.nodeSize - 1,
              () => {
                const label = document.createElement('button')
                label.type = 'button'
                label.contentEditable = 'false'
                label.dataset.test = 'meeting-task.block-annotation'
                label.dataset.taskToken = block.token
                label.className = 'ml-2 inline-block rounded bg-indigo-50 px-2 py-1.5 text-xs text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200'
                const people = (block.node.attrs['data-meeting-task-people'] || '').split(',').filter(Boolean)
                  .map(id => options.personLabels?.[`person.${id}`] || options.missingPersonLabel || '')
                  .filter(Boolean).join(', ')
                label.textContent = [options.taskLabel || 'Task', people, block.node.attrs['data-meeting-task-due']]
                  .filter(Boolean).join(' · ')
                label.setAttribute('aria-label', label.textContent)
                label.addEventListener('mousedown', event => event.preventDefault())
                label.addEventListener('click', event => {
                  event.preventDefault()
                  open(editor, block.token)
                })
                return label
              }, { key: JSON.stringify([block.token, block.node.attrs['data-meeting-task-people'], block.node.attrs['data-meeting-task-due']]), side: 1, stopEvent: () => true }
            )))
          }
        }
      })]
    }
  })
}

export const meetingTaskCommands = [
  'prepareMeetingTask', 'stageMeetingTask', 'cancelMeetingTask', 'markMeetingTask', 'setMeetingTask', 'unmarkMeetingTask', 'setMeetingTaskContext', 'finishMeetingTaskEdit'
]
